// ============================================================
// useCurrentUser – Wrapper hook lấy thông tin user từ AuthContext
// v2: Thêm multi-role support (allRoleCodes, isMultiRole)
// ============================================================

import { useEffect } from 'react';
import { useAuth } from '@/core/auth/AuthContext';
import { useUserRoles } from '@/core/roles/useUserRoles';
import type { UserRole } from '@/config/navigation.config';
import { useImpersonation } from '@/core/auth/ImpersonationContext';

const ROLE_CACHE_KEY = 'lscm_cached_role';
const PROFILE_CACHE_KEY = 'lscm_cached_profile';

function getCachedRole(): UserRole {
  try {
    const cached = sessionStorage.getItem(ROLE_CACHE_KEY);
    return (cached as UserRole) || 'staff';
  } catch {
    return 'staff';
  }
}

interface CachedProfile {
  id: string;
  fullName: string;
  email: string;
  phone?: string;
  avatarPath?: string;
  roleCode: UserRole;
  jobTitle?: string;
  isActive: boolean;
  companyId: string;
}

function getCachedProfile(): CachedProfile | null {
  try {
    const raw = sessionStorage.getItem(PROFILE_CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as CachedProfile;
  } catch {
    return null;
  }
}

export function useCurrentUser() {
  const { profile, isLoading: authLoading } = useAuth();
  const { impersonatedUser, isImpersonating } = useImpersonation();
  
  // Nếu đang mô phỏng, dùng dữ liệu của người bị mô phỏng
  const effectiveProfile = isImpersonating && impersonatedUser ? impersonatedUser : profile;

  // Cache profile
  useEffect(() => {
    if (profile) {
      try {
        sessionStorage.setItem(ROLE_CACHE_KEY, profile.role_code);
        const cacheData: CachedProfile = {
          id: profile.id,
          fullName: profile.full_name,
          email: profile.email ?? '',
          phone: profile.phone ?? undefined,
          avatarPath: profile.avatar_path ?? undefined,
          roleCode: profile.role_code as UserRole,
          jobTitle: profile.job_title ?? undefined,
          isActive: profile.is_active,
          companyId: profile.company_id || '',
        };
        sessionStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(cacheData));
      } catch { /* ignore */ }
    }
  }, [effectiveProfile]);

  const user: CachedProfile = effectiveProfile
    ? {
        id: effectiveProfile.id,
        fullName: effectiveProfile.full_name,
        email: effectiveProfile.email ?? '',
        phone: effectiveProfile.phone ?? undefined,
        avatarPath: effectiveProfile.avatar_path ?? undefined,
        roleCode: effectiveProfile.role_code as UserRole,
        jobTitle: effectiveProfile.job_title ?? undefined,
        isActive: effectiveProfile.is_active,
        companyId: effectiveProfile.company_id || '',
      }
    : (getCachedProfile() ?? {
        id: '',
        fullName: 'Đang tải...',
        email: '',
        phone: undefined,
        avatarPath: undefined,
        roleCode: getCachedRole(),
        jobTitle: undefined,
        isActive: true,
        companyId: '',
      });

  // Multi-role support
  const { allRoleCodes, isMultiRole, loading: rolesLoading } = useUserRoles(
    user.id || null,
    user.roleCode
  );

  const hasRole = (roles: UserRole[]): boolean => {
    return allRoleCodes.some(r => roles.includes(r));
  };

  return {
    user,
    hasRole,
    isLoading: authLoading || rolesLoading,
    // Multi-role
    allRoleCodes,
    isMultiRole,
  };
}
